import type {
    NewCarCompareAddCarType,
    NewCarCompareClearAllType,
    NewCarCompareRemoveCarType,
    NewCarCompareSetFinanceProductType,
} from "../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import type {
    CompareMotorizationType,
    ModelGradeBodyTypeIdsType,
    CarFinanceDataType,
    EquipmentSpecNameIdType,
    SpecItemType,
} from "../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import type { NcCompareContext } from "../../../../common-deprecated/types/NewCarCompareTypes";
import type { FilteredCarType } from "../../../../common-deprecated/types/FilterApiType";
import type { EquipmentSpecItemType, SelectGradeType, SetCarDataType } from "../../utils/compareTypes";

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------

export const NEW_CAR_COMPARE_SET_AVAILABLE_GRADES = "newCarCompare/setAvailableGrades" as const;
export const NEW_CAR_COMPARE_SET_CAR_DATA = "newCarCompare/setCarData" as const;
export const NEW_CAR_COMPARE_SET_PARSED_CAR_DATA = "newCarCompare/setParsedCarData" as const;
export const NEW_CAR_COMPARE_SET_NEW_ENGINE = "newCarCompare/setNewEngine" as const;
export const NEW_CAR_COMPARE_GET_ETA = "newCarCompare/getEta" as const;
export const NEW_CAR_COMPARE_SET_ETA = "newCarCompare/setEta" as const;
export const NEW_CAR_COMPARE_SET_CAR_TO_ADD = "newCarCompare/setCarToAdd" as const;
export const NEW_CAR_COMPARE_CHANGE_COLOUR = "newCarCompare/changeColour" as const;
export const NEW_CAR_COMPARE_LOAD_FINANCE = "newCarCompare/loadFinance" as const;
export const NEW_CAR_COMPARE_SET_FINANCE = "newCarCompare/updateFinance" as const;
export const NEW_CAR_COMPARE_SET_NC_COMPARE_CONTEXT = "newCarCompare/setNcCompareContext" as const;
export const NEW_CAR_COMPARE_SET_FILTERED_CAR_LIST = "newCarCompare/setFilteredCarList" as const;

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------

type NewCarCompareSetAvailableGradesType = {
    type: typeof NEW_CAR_COMPARE_SET_AVAILABLE_GRADES;
    available: SelectGradeType[] | null;
};
export type NewCarCompareSetParsedCarDataType = {
    type: typeof NEW_CAR_COMPARE_SET_PARSED_CAR_DATA;
    specs: SpecItemType[];
    equipment: EquipmentSpecItemType[];
    packs: EquipmentSpecNameIdType[];
};
export type NewCarCompareSetCarDataType = {
    type: typeof NEW_CAR_COMPARE_SET_CAR_DATA;
    data: SetCarDataType;
    index: number;
};

export type NewCarCompareSetNewEngineType = {
    type: typeof NEW_CAR_COMPARE_SET_NEW_ENGINE;
    motorization: CompareMotorizationType;
    index: number;
};
export type NewCarCompareSetCarToAddType = {
    type: typeof NEW_CAR_COMPARE_SET_CAR_TO_ADD;
    modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType;
};
export type NewCarCompareGetEtaType = {
    type: typeof NEW_CAR_COMPARE_GET_ETA;
    modelId: string;
    carId: string;
    index: number;
    colourId: string;
    upholsteryId: string;
};
export type NewCarCompareSetEtaType = {
    type: typeof NEW_CAR_COMPARE_SET_ETA;
    index: number;
    etaDate: string | false;
    errorMessage: string;
};
export type NewCarCompareChangeColourType = {
    type: typeof NEW_CAR_COMPARE_CHANGE_COLOUR;
    colourCode: string;
    index: number;
};

export type NewCarCompareLoadFinanceType = {
    type: typeof NEW_CAR_COMPARE_LOAD_FINANCE;
    index: number;
};

export type NewCarCompareSetFinanceType = {
    type: typeof NEW_CAR_COMPARE_SET_FINANCE;
    data: CarFinanceDataType;
    index: number;
};

export type NewCarCompareSetNcCompareContext = {
    type: typeof NEW_CAR_COMPARE_SET_NC_COMPARE_CONTEXT;
    ncCompareContext: NcCompareContext;
};

export type NewCarCompareSetFilteredCarListType = {
    type: typeof NEW_CAR_COMPARE_SET_FILTERED_CAR_LIST;
    filteredCarList: FilteredCarType[];
};

// ----------------------------------------------------------------------
// Actions
// ----------------------------------------------------------------------

export const compareSetAvailableGrades = (
    available: SelectGradeType[] | null,
): NewCarCompareSetAvailableGradesType => ({
    type: NEW_CAR_COMPARE_SET_AVAILABLE_GRADES,
    available,
});

export const compareSetCarData = (data: SetCarDataType, index: number): NewCarCompareSetCarDataType => ({
    type: NEW_CAR_COMPARE_SET_CAR_DATA,
    data,
    index,
});

export const compareSetParsedData = (
    specs: SpecItemType[],
    equipment: EquipmentSpecItemType[],
    packs: EquipmentSpecNameIdType[],
): NewCarCompareSetParsedCarDataType => ({
    type: NEW_CAR_COMPARE_SET_PARSED_CAR_DATA,
    specs,
    equipment,
    packs,
});

export const compareSetNewEngine = (
    motorization: CompareMotorizationType,
    index: number,
): NewCarCompareSetNewEngineType => ({
    type: NEW_CAR_COMPARE_SET_NEW_ENGINE,
    motorization,
    index,
});

export const compareGetEta = (
    modelId: string,
    carId: string,
    index: number,
    colourId: string,
    upholsteryId: string,
): NewCarCompareGetEtaType => ({
    type: NEW_CAR_COMPARE_GET_ETA,
    modelId,
    carId,
    colourId,
    upholsteryId,
    index,
});

export const compareSetEta = (
    index: number,
    etaDate: string | false,
    errorMessage: string,
): NewCarCompareSetEtaType => ({
    type: NEW_CAR_COMPARE_SET_ETA,
    index,
    etaDate,
    errorMessage,
});

export const compareSetNcCompareContext = (ncCompareContext: NcCompareContext): NewCarCompareSetNcCompareContext => ({
    type: NEW_CAR_COMPARE_SET_NC_COMPARE_CONTEXT,
    ncCompareContext,
});

// Only used in logic
export const compareSetCarToAdd = (modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType): NewCarCompareSetCarToAddType => ({
    type: NEW_CAR_COMPARE_SET_CAR_TO_ADD,
    modelGradeBodyTypeIds,
});

export const compareChangeColour = (colourCode: string, index: number): NewCarCompareChangeColourType => ({
    type: NEW_CAR_COMPARE_CHANGE_COLOUR,
    colourCode,
    index,
});

export const compareLoadFinance = (index: number): NewCarCompareLoadFinanceType => ({
    type: NEW_CAR_COMPARE_LOAD_FINANCE,
    index,
});

export const compareSetFinance = (data: CarFinanceDataType, index: number): NewCarCompareSetFinanceType => ({
    type: NEW_CAR_COMPARE_SET_FINANCE,
    data,
    index,
});

export const compareSetFilteredCarList = (filteredCarList: FilteredCarType[]): NewCarCompareSetFilteredCarListType => ({
    type: NEW_CAR_COMPARE_SET_FILTERED_CAR_LIST,
    filteredCarList,
});

export type NewCarCompareActionsType =
    | NewCarCompareGetEtaType
    | NewCarCompareSetEtaType
    | NewCarCompareSetAvailableGradesType
    | NewCarCompareAddCarType
    | NewCarCompareSetCarDataType
    | NewCarCompareRemoveCarType
    | NewCarCompareSetParsedCarDataType
    | NewCarCompareSetNewEngineType
    | NewCarCompareClearAllType
    | NewCarCompareSetFinanceProductType
    | NewCarCompareSetCarToAddType
    | NewCarCompareChangeColourType
    | NewCarCompareLoadFinanceType
    | NewCarCompareSetFinanceType
    | NewCarCompareSetNcCompareContext
    | NewCarCompareSetFilteredCarListType;
